/**
 * "in this state for **3h 27m**" — the chip that sits beside the status pill and
 * says how long the contest has been where it is (design-system.md §6 "Age
 * chip"; `.age-chip` in the prototype's contest-overview.css).
 *
 * Takes the verb and the formatted duration rather than a timestamp. The chip
 * has no business deciding what "now" is: the Contest Overview refreshes its age
 * on a 30s interval while the contest is draft/scheduled/live, and a component
 * that read the clock itself would either render stale or need its own timer.
 * `formatDuration` turns the span into the text.
 */
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';
import { ClockIcon } from './line-icons';

interface AgeChipProps {
  /**
   * The leading phrase, e.g. "in this state for" or "in draft for".
   *
   * Optional, because the event-date chip beside it is the same chip with
   * nothing to say before its value — see `icon`.
   */
  verb?: string;
  /** Pre-formatted, from `formatDuration`. */
  duration: string;
  /**
   * Replaces the clock, for a chip that is not about elapsed time.
   *
   * The Contest Overview's meta row puts an event date next to the age using
   * the handoff's same `.age-chip` rule — identical size, colour and gap, a
   * calendar glyph instead of a clock. One component rather than a near-copy,
   * because the design has one rule for both. Pass an icon sized to match:
   * 13px, 0.7 opacity, as the default below.
   */
  icon?: ReactNode;
  /**
   * Names the chip for assistive tech — required in spirit whenever `verb` is
   * absent.
   *
   * The icon is decorative and hidden, so a chip with no verb reads out as a
   * bare value: "11 Jun 2026", with nothing saying what the date *is*. The verb
   * carries that meaning when there is one, and this carries it when there is
   * not.
   */
  label?: string;
}

export const AgeChip = ({ verb, duration, icon, label }: AgeChipProps) => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      color: theme.tokens.color.ink3,
      fontSize: '13px',
      fontVariantNumeric: 'tabular-nums',
    })}
  >
    {/* 13px is the handoff's size for this glyph, and 0.7 its opacity. */}
    {icon ?? <ClockIcon sx={{ fontSize: '13px', opacity: 0.7, flexShrink: 0 }} />}
    {/*
      Real text clipped out of view, not `aria-label`: the chip is a `<span>`,
      so its implicit role is `generic`, and ARIA prohibits naming one — real
      assistive tech drops the attribute (the same trap `header-action.tsx`
      documents for its disabled-button wrapper). A hidden text node is
      announced in reading order wherever an attribute would not be.
    */}
    {label ? (
      <Box
        component="span"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {label}:{' '}
      </Box>
    ) : null}
    {/*
      The separating space rides with the verb rather than standing between the
      two expressions. A chip with no verb would otherwise render a leading
      space before its value — invisible next to the icon's own gap, but it is
      still a text node the value has to share its line box with.
    */}
    {verb ? `${verb} ` : null}
    <Box
      component="strong"
      sx={(theme: Theme) => ({ color: theme.tokens.color.ink2, fontWeight: 600 })}
    >
      {duration}
    </Box>
  </Box>
);
